/**
 * @file ConfirmationPage.tsx
 *
 * @description This page allows officers to download submitted reports and provides confirmation of
 * successful submission with event numbers when available.
 *
 * @module Views
 * @requires React
 * @requires Material-UI
 * @requires react-router-dom
 * @requires file-saver
 */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Download } from "@mui/icons-material";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import saveAs from "file-saver";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { DownloadReportModal } from "../../modals/DownloadReportModal";
import { FeedbackBox } from "../../components/feedback/FeedbackBox";
import JobMonitor from "../../components/jobs/JobMonitor";
import { rmsClient } from "../../api/clients";

/**
 * @name ConfirmationPage
 *
 * @description Confirmation page component that displays after successful report submission and
 * provides functionality to download the submitted report and navigate back to the home page.
 *
 * @component
 * @returns {JSX.Element} The rendered confirmation page
 */
const ConfirmationPage: React.FC = () => {
  // React Router hooks for navigation and accessing URL parameters
  const navigate = useNavigate();
  const { eventNumber } = useParams();
  const location = useLocation() as { state?: { reportID?: string } };
  const theme = useTheme();

  // Safely extract reportID from location state
  const reportID = location.state?.reportID;

  // Redirect to home if user manually navigated here without the required state
  useEffect(() => {
    if (!reportID) {
      navigate("/", { replace: true });
    }
  }, [reportID, navigate]);

  // State to control the visibility of the language selection modal for report download
  const [showDownloadLanguageModal, setShowDownloadLanguageModal] =
    useState(false);

  // Custom hook that provides snackbar notification functionality for error handling
  const { snackBarMessage, snackBarMode, showError, handleCloseSnackbar } =
    useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  /**
   * @name handleReturnHome
   *
   * @description Handles navigation back to the home page
   */
  const handleReturnHome = () => {
    navigate("/");
  };

  /**
   * @name handleDownloadIncident
   *
   * @description Handles the download of the incident report document and
   * downloads the report as a .docx file with the incident number in the filename
   *
   * @returns {Promise<void>} Promise that resolves when download completes or rejects on error
   */
  const handleDownloadIncident = (languageCode: string) => {
    if (!reportID) {
      // Defensive guard (should be redirected already)
      showError("Invalid navigation. Redirecting to home.");
      navigate("/", { replace: true });
      return Promise.resolve();
    }

    return rmsClient
      .get("/report/download", {
        params: { reportId: reportID, languageCode: languageCode },
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
        },
      })
      .then((response) => {
        // Create a blob from the response data with appropriate MIME type
        const blob = new Blob([response.data], {
          type: "application/pdf",
        });

        const humanFriendlyDate = new Date().toISOString().split("T")[0];
        // Use file-saver to trigger browser download with formatted filename
        saveAs(blob, `incident-report-${humanFriendlyDate}.pdf`);

        // Close the download language modal after successful download
        setShowDownloadLanguageModal(false);
      })
      .catch((error) => {
        // Show error message using the error snackbar
        showError(
          error.response?.data?.error ||
            "An error occurred while downloading the document. Please try again later."
        );
      });
  };

  return (
    <>
      <JobMonitor />
      {/* Snackbar component for displaying success/error messages */}
      <SnackBarMessage
        message={snackBarMessage}
        mode={snackBarMode}
        handleClose={handleCloseSnackbar}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />

      {/* Modal for language selection when downloading report */}
      <DownloadReportModal
        handleDownloadIncident={handleDownloadIncident}
        showDownloadLanguageModal={showDownloadLanguageModal}
        handleCloseDownloadModal={() => setShowDownloadLanguageModal(false)}
      />

      {/* Main content */}
      <Container
        maxWidth="xl"
        sx={{
          pb: 4,
          minHeight: "calc(100vh - 100px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grid
          container
          spacing={3}
          direction="column"
          sx={{ alignItems: "center", textAlign: "center" }}
        >
          {/* Success Card */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.success.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                mx: "auto",
                animation: "popIn 0.6s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    opacity: 0,
                    transform: "scale(0)",
                  },
                  "70%": {
                    transform: "scale(1.1)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                },
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 144,
                  color: theme.palette.success.main,
                }}
              />
            </Box>

            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Your Report is Ready
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2, maxWidth: 500, mx: "auto" }}
            >
              Your police report is now signed and downloadable using the button
              below.
            </Typography>

            {eventNumber && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography variant="body1">
                  Your report has been submitted to your RMS with event number:{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {eventNumber}
                  </Box>
                </Typography>
              </Paper>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 2,
                width: "100%",
                maxWidth: 500,
                justifyContent: "center",
                mx: "auto",
              }}
            >
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => setShowDownloadLanguageModal(true)}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: `0 4px 14px ${alpha(
                    theme.palette.primary.main,
                    0.35
                  )}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.45
                    )}`,
                  },
                }}
              >
                Download
              </Button>

              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={handleReturnHome}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  borderWidth: 2,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderWidth: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Home
              </Button>
            </Box>
          </Grid>

          {/* Feedback Section */}
          {reportID && (
            <Grid size={{ xs: 12 }}>
              <FeedbackBox reportID={reportID} />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default ConfirmationPage;
